import { ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconFolder,
  IconGlobe,
  IconBook,
  IconCredit,
  IconCog,
  IconCube,
  IconChevronL,
  IconChevronR,
  IconMark,
  IconPackage,
  IconServer,
  IconStar,
} from './Icons';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';

export type SidebarKey =
  | 'projects'
  | 'community'
  | 'docs'
  | 'billing'
  | 'settings'
  | 'pricing'
  | 'admin-assets'
  | 'admin-featured'
  | 'admin-settings'
  | 'admin-units';

interface SidebarProps {
  active?: SidebarKey;
  credits?: number;
  creditsMax?: number;
  projectsUsed?: number;
  projectsMax?: number;
  defaultCollapsed?: boolean;
  onCommunityClick?: () => void;
  onSettingsClick?: () => void;
  onBillingClick?: () => void;
}

export default function Sidebar({
  active = 'projects',
  credits,
  creditsMax,
  projectsUsed,
  projectsMax,
  defaultCollapsed = false,
  onCommunityClick,
  onSettingsClick,
  onBillingClick,
}: SidebarProps) {
  const isMobile = useIsMobile();
  const [collapsedState, setCollapsed] = useState(defaultCollapsed);
  const collapsed = isMobile || collapsedState;
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const width = collapsed ? 60 : 220;

  const knownCredits = typeof credits === 'number' && typeof creditsMax === 'number' && creditsMax > 0;
  const knownProjects =
    typeof projectsUsed === 'number' && typeof projectsMax === 'number' && projectsMax > 0;

  const Item = ({
    k,
    ic,
    label,
    badge,
    onClick,
  }: {
    k: SidebarKey;
    ic: ReactNode;
    label: string;
    badge?: number | string;
    onClick?: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={'side-link ' + (active === k ? 'active' : '')}
      title={collapsed ? label : undefined}
      style={{
        justifyContent: collapsed ? 'center' : 'flex-start',
        padding: collapsed ? 0 : '0 10px',
        width: collapsed ? 36 : '100%',
        border: 'none',
        background: active === k ? undefined : 'transparent',
      }}
    >
      <span className="ic">{ic}</span>
      {!collapsed && (
        <>
          <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
          {badge != null && <span className="count tnum">{badge}</span>}
        </>
      )}
    </button>
  );

  return (
    <aside
      className="sidebar"
      style={{ width, padding: collapsed ? '18px 12px' : '18px 14px' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 6,
          padding: collapsed ? '4px 0' : '4px 8px',
          marginBottom: 6,
        }}
      >
        {!collapsed && (
          <Link to="/profile" className="brand" style={{ fontSize: 15, gap: 8 }}>
            <IconMark size={20} />
            Entire
          </Link>
        )}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? t('sidebar.expand', 'Expand sidebar') : t('sidebar.collapse', 'Collapse sidebar')}
          className="btn ghost sm icon"
          style={{ width: 24, height: 24, padding: 0, border: 'none' }}
        >
          {collapsed ? <IconChevronR /> : <IconChevronL />}
        </button>
      </div>

      {!collapsed && <div className="side-section">{t('sidebar.workspace', 'Workspace')}</div>}
      <Item k="projects" ic={<IconFolder />} label={t('sidebar.projects', 'Projects')} badge={knownProjects ? projectsUsed : undefined} onClick={() => navigate('/profile')} />
      <Item k="community" ic={<IconGlobe />} label={t('sidebar.community', 'Community')} onClick={onCommunityClick} />
      <Item k="docs" ic={<IconBook />} label={t('sidebar.docs', 'Docs')} onClick={() => navigate('/docs')} />

      {!collapsed && <div className="side-section">{t('sidebar.account', 'Account')}</div>}
      <Item k="billing" ic={<IconCredit />} label={t('sidebar.credits', 'Credits')} badge={knownCredits ? credits : undefined} onClick={onBillingClick} />
      <Item k="settings" ic={<IconCog />} label={t('sidebar.settings', 'Settings')} onClick={onSettingsClick} />

      {user?.isAdmin && (
        <>
          {!collapsed && <div className="side-section">Admin</div>}
          <Item
            k="admin-assets"
            ic={<IconPackage />}
            label="Asset Manager"
            onClick={() => navigate('/admin/assets')}
          />
          <Item
            k="admin-featured"
            ic={<IconStar size={16} />}
            label="Featured games"
            onClick={() => navigate('/admin/featured')}
          />
          <Item
            k="admin-settings"
            ic={<IconServer />}
            label="Server settings"
            onClick={() => navigate('/admin/settings')}
          />
          <Item
            k="admin-units"
            ic={<IconCube />}
            label="Units"
            onClick={() => navigate('/admin/units')}
          />
        </>
      )}

      <div style={{ flex: 1 }} />

      {!collapsed && (knownCredits || knownProjects) && (
        <div style={{ padding: 12, border: '1px solid var(--line)', borderRadius: 10, fontSize: 12 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            {t('sidebar.betaProgram', 'Beta program')}
          </div>
          <div className="muted-2" style={{ lineHeight: 1.45, marginBottom: 12 }}>
            {t('sidebar.betaNote', "You're using Entire while it's free.")}
          </div>

          {knownCredits && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-5)', marginBottom: 4 }}>
                <span>{t('sidebar.credits', 'Credits')}</span>
                <span className="mono tnum">
                  {credits} / {creditsMax}
                </span>
              </div>
              <div
                style={{
                  height: 3,
                  borderRadius: 99,
                  background: 'rgba(255,255,255,0.06)',
                  overflow: 'hidden',
                  marginBottom: knownProjects ? 10 : 0,
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, (credits! / creditsMax!) * 100)}%`,
                    height: '100%',
                    background: 'var(--accent)',
                  }}
                />
              </div>
            </>
          )}

          {knownProjects && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-5)', marginBottom: 4 }}>
                <span>{t('sidebar.projects', 'Projects')}</span>
                <span className="mono tnum">
                  {projectsUsed} / {projectsMax}
                </span>
              </div>
              <div style={{ height: 3, borderRadius: 99, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${Math.min(100, (projectsUsed! / projectsMax!) * 100)}%`,
                    height: '100%',
                    background: 'var(--accent)',
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}
    </aside>
  );
}
